import { useEffect, useState } from "react";
import { doc, getDoc, collection, query, where, onSnapshot } from "firebase/firestore";
import { db, signInAnonymouslyForField } from "../firebase";
import { S, C } from "../ui";
import AssetCheckIn from "../core/AssetCheckIn";
import lockupDark from "../assets/lockup-dark.svg";

// The field-worker experience reached via QR scan (?facilityId=&
// assetId= in the URL, see App.jsx). Handles worker identity (pick name
// → PIN, mirroring CustodialView's own team-member flow — see
// firestore.rules' workers comment for why this is a UX check, not a
// security boundary) then hands off to AssetCheckIn.jsx (Phase 11
// extraction) for the actual checklist/clock-in screen. Staff reaching
// the app via the general Staff Home login (no QR code) go through
// StaffAuthScreen.jsx + AssetPicker.jsx instead, landing on the same
// AssetCheckIn.
//
// Auth: anonymous (see src/firebase.js header for why this is safe here).
export default function FieldFlow({ facilityId, assetId }) {
  const [ready, setReady] = useState(false);
  const [asset, setAsset] = useState(undefined); // undefined = not fetched yet, null = doesn't exist
  const [workers, setWorkers] = useState([]);
  const [step, setStep] = useState("pickWorker"); // pickWorker | pin | main
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [pin, setPin] = useState("");
  const [pinErr, setPinErr] = useState("");

  useEffect(() => { signInAnonymouslyForField().then(() => setReady(true)); }, []);

  useEffect(() => {
    if (!ready) return;
    getDoc(doc(db, "assets", assetId)).then(s => setAsset(s.exists() ? { id: s.id, ...s.data() } : null));
  }, [ready, assetId]);

  useEffect(() => {
    if (!ready) return;
    return onSnapshot(query(collection(db, "workers"), where("facilityId", "==", facilityId)), s =>
      setWorkers(s.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [ready, facilityId]);

  const matchingWorkers = asset ? workers.filter(w => w.trade === asset.trade) : [];

  const verifyPin = () => {
    if (pin !== selectedWorker.pin) { setPinErr("Wrong PIN"); setPin(""); return; }
    setPinErr(""); setStep("main");
  };

  if (!ready || asset === undefined) return <Shell><div style={{ textAlign: "center", color: C.muted }}>Loading…</div></Shell>;
  if (asset === null) return <Shell><div style={{ textAlign: "center", color: C.red }}>This QR code isn't valid — the asset it points to doesn't exist.</div></Shell>;

  if (step === "pickWorker") return (
    <Shell>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 4 }}>{asset.trade}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: C.navy, marginBottom: 16 }}>{asset.name}</div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 10 }}>Who's this?</div>
      {matchingWorkers.length === 0 && <div style={{ fontSize: 13, color: C.muted }}>No {asset.trade} workers set up for this facility yet — ask your admin.</div>}
      {matchingWorkers.map(w => (
        <div key={w.id} onClick={() => { setSelectedWorker(w); setStep("pin"); }}
          style={{ padding: "12px 14px", borderRadius: 10, border: `1.5px solid ${C.border}`, background: C.bg, marginBottom: 8, cursor: "pointer", fontWeight: 500 }}>
          {w.name}
        </div>
      ))}
    </Shell>
  );

  if (step === "pin") return (
    <Shell>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Hi, {selectedWorker.name.split(" ")[0]}</div>
      {pinErr && <div style={S.err}>{pinErr}</div>}
      <input style={S.input} type="password" placeholder="Your PIN" value={pin} onChange={e => setPin(e.target.value)} onKeyDown={e => e.key === "Enter" && verifyPin()} autoFocus maxLength={8} />
      <button style={S.btn()} onClick={verifyPin}>Continue →</button>
      <button style={S.ghostBtn} onClick={() => { setStep("pickWorker"); setSelectedWorker(null); setPin(""); }}>← Back</button>
    </Shell>
  );

  // step === "main"
  return (
    <Shell wide>
      <AssetCheckIn facilityId={facilityId} assetId={assetId} worker={selectedWorker} />
    </Shell>
  );
}

function Shell({ children, wide }) {
  return (
    <div style={{ minHeight: "100vh", background: C.navy, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <img src={lockupDark} alt="Edifice View" style={{ height: 28, marginBottom: 20, opacity: 0.9 }} />
      <div style={{ background: C.white, borderRadius: 16, padding: "28px 24px", width: "100%", maxWidth: wide ? 480 : 380, boxShadow: "0 24px 64px rgba(0,0,0,0.3)" }}>
        {children}
      </div>
    </div>
  );
}
